import { resolveAdditionalCostFlags } from './pricingEngine';

const list = (value) => Array.isArray(value) ? value : [];
const number = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const cents = (value) => Math.round(number(value) * 100);
const chargeTotal = (row) => row?.Total != null
  ? number(row.Total)
  : number(row?.Retail) * number(row?.Quantity);
const displayCharges = (decoration) => list(decoration?.Charge);
const includedCharge = (row) => resolveAdditionalCostFlags(row).includeInPrice;
const visibleCharge = (row) => {
  const flags = resolveAdditionalCostFlags(row);
  return flags.displayToClient && !flags.includeInPrice && !flags.subtractFromMargin;
};
const allMoneyRows = (rows) => [...rows.prices, ...rows.decorations, ...rows.artworks];

// Invoice-only presentation fallback for historical artwork imprint routing codes.
// The canonical SO decoration/artwork data and all monetary rows remain untouched.
const isImprintRoutingCode = (value) =>
  /^(supplier\|\$|thirdparty\|)/i.test(String(value || '').trim());

const resolveLegacyInvoiceImprintLabel = (decoration) => {
  for (const artwork of [
    decoration?.ArtWork,
    decoration?.artwork,
    decoration?.documentDecoration?.artwork,
  ]) {
    const imprintType = String(artwork?.ImprintType ?? artwork?.imprintType ?? '').trim();
    if (imprintType && !isImprintRoutingCode(imprintType)) return imprintType;
  }
  const savedMethodName = String(
    decoration?.DecoratorDetails?.methodName ??
    decoration?.decoratorDetails?.methodName ??
    decoration?.routing?.methodName ??
    decoration?.documentDecoration?.method?.name ??
    ''
  ).trim();
  return isImprintRoutingCode(savedMethodName)
    ? ''
    : savedMethodName.replace(/\s*\(Supplier\)\s*$/i, '').trim();
};

export const buildInvoiceProductDisplayRows = (product = {}) => {
  const pricings = list(product.pricings);
  const decorations = list(product.decorations);
  const artworks = list(product.artworks?.length ? product.artworks : product.ArtWorks);
  const quantity = pricings.reduce((sum, row) => sum + number(row.Quantity), 0);
  const taxRate = number(product?.taxDetails?.rate);
  const rate = taxRate > 0 && taxRate <= 1 ? taxRate : 0;
  const taxLabel = product?.taxDetails?.label || product?.taxDetails?.name ||
    product?.taxDetails?.taxName || product?.taxDetails?.title ||
    (rate > 0 ? 'Tax (' + (rate * 100).toFixed(2) + '%)' : 'Tax');

  const charges = decorations.flatMap(displayCharges);
  const separateTotal = charges.filter(visibleCharge).reduce((sum, row) => sum + chargeTotal(row), 0);
  const includedTotal = charges.filter(includedCharge).reduce((sum, row) => sum + chargeTotal(row), 0);
  const pricingTotal = pricings.reduce((sum, row) => sum +
    (row.Total == null ? number(row.Retail) * number(row.Quantity) : number(row.Total)), 0);
  const savedSubtotalRaw = product.Subtotal ?? product.subtotal;
  const savedSubtotal = savedSubtotalRaw == null ? NaN : Number(savedSubtotalRaw);
  const bundledAmount = Number.isFinite(savedSubtotal) &&
    Math.abs(savedSubtotal - pricingTotal - separateTotal) <= 0.01 ? 0 : includedTotal;

  const prices = pricings.map((row) => {
    const qty = number(row.Quantity);
    const unit = number(row.Retail) + (quantity > 0 ? bundledAmount / quantity : 0);
    const subtotal = unit * qty;
    const size = String(row.Size || '');
    const color = String(row.Color || '');
    return {
      id: product.ProductId, item: size + (size && color ? ' - ' : '') + color,
      price: unit, quantity: qty, subtotal, taxLabel,
      taxAmount: subtotal * rate, amount: subtotal * (1 + rate),
    };
  });
  const billedDecorations = decorations.flatMap((decoration) =>
    displayCharges(decoration).filter(visibleCharge).map((row) => {
      const qty = number(row.Quantity);
      const unit = number(row.Retail);
      const subtotal = row.Retail == null ? chargeTotal(row) : unit * qty;
      return {
        decoration: decoration.Location, item: row.Name, price: unit, quantity: qty,
        subtotal, taxLabel, taxAmount: subtotal * rate, amount: subtotal * (1 + rate),
      };
    })
  );
  const artworkRows = artworks.map((artwork) => {
    const unit = number(artwork.Charge);
    const subtotal = unit * quantity;
    const decorationId = artwork.DecorationId ?? artwork.decorationId;
    const matched = decorationId == null ? null : decorations.find((decoration) =>
      String(decoration.id ?? decoration.Id) === String(decorationId));
    // A zero-dollar artwork is not necessarily bundled. Trust the linked SO
    // decoration's actual pricing mode before displaying "Included".
    const includedInPrice = unit === 0 &&
      Boolean(matched && displayCharges(matched).some(includedCharge));
    const originalImprintType = String(artwork.ImprintType || '');
    const isLegacyRoutingCode = isImprintRoutingCode(originalImprintType);
    const imprintLabel = isLegacyRoutingCode
      ? resolveLegacyInvoiceImprintLabel(matched)
      : originalImprintType;
    return {
      decoration: '', item: isLegacyRoutingCode && !imprintLabel
        ? 'Imprint Cost' : 'Imprint Cost - ' + imprintLabel,
      price: unit, quantity, subtotal, taxLabel,
      taxAmount: subtotal * rate, amount: subtotal * (1 + rate), includedInPrice,
    };
  });
  return { prices, decorations: billedDecorations, artworks: artworkRows, rate };
};

/** Allocate each tax group's cents by largest remainder, preserving every
 * customer's printed subtotal + printed tax = printed total. */
export const allocateInvoiceLineTaxCents = (rows, rate, desiredTaxCents = null) => {
  const source = list(rows);
  if (source.some((row) => cents(row.subtotal) < 0)) {
    // Credits/returns use signed values. Do not zero them or redistribute a
    // positive invoice-wide cent onto a negative selling line.
    return source.map((row) => {
      const subtotal = cents(row.subtotal);
      const tax = cents(row.taxAmount ?? ((subtotal / 100) * number(rate)));
      return { ...row, subtotal: subtotal / 100, taxAmount: tax / 100,
        amount: (subtotal + tax) / 100 };
    });
  }
  const sub = source.map((row) => Math.max(0, cents(row.subtotal)));
  const exact = sub.map((value) => value * Math.max(0, number(rate)));
  const floors = exact.map((value) => Math.floor(value + 1e-9));
  const min = floors.reduce((sum, value) => sum + value, 0);
  const requested = desiredTaxCents == null
    ? Math.round(exact.reduce((sum, value) => sum + value, 0))
    : Number(desiredTaxCents);
  const target = Number.isInteger(requested) && requested >= 0 ? requested : min;
  const result = [...floors];
  const remainder = target - min;
  if (remainder >= 0 && remainder <= source.length) {
    const priority = exact.map((value, i) => ({ i, fraction: value - floors[i] }))
      .sort((a, b) => b.fraction - a.fraction || a.i - b.i);
    for (let i = 0; i < remainder; i += 1) result[priority[i].i] += 1;
  } else {
    exact.forEach((value, i) => { result[i] = Math.round(value); });
  }
  return source.map((row, i) => ({
    ...row, subtotal: sub[i] / 100, taxAmount: result[i] / 100,
    amount: (sub[i] + result[i]) / 100,
  }));
};

const setMoneyRows = (display, monetary) => ({
  ...display,
  prices: monetary.slice(0, display.prices.length),
  decorations: monetary.slice(display.prices.length, display.prices.length + display.decorations.length),
  artworks: monetary.slice(display.prices.length + display.decorations.length),
});

/** A read-only display projection. Saved invoice, SO and QBO values are never changed. */
export const buildInvoiceDisplayPlan = ({ invoice = {}, services = [], totalsCents, taxBreakdown = [] } = {}) => {
  const products = list(invoice.items);
  const parsedRelationship = (() => {
    const raw = invoice.invoiceRelationships || {};
    if (typeof raw !== 'string') return raw;
    try { return JSON.parse(raw) || {}; } catch (_) { return {}; }
  })();
  const role = String(parsedRelationship.role || parsedRelationship.type || invoice.invoiceRole || '').toLowerCase();
  const displays = products.map(buildInvoiceProductDisplayRows);
  const fallback = () => displays.map((display) =>
    setMoneyRows(display, allocateInvoiceLineTaxCents(allMoneyRows(display), display.rate)));

  // Never force full-order tax onto deposits, progress invoices or taxable
  // adjustments. Multi-tax documents retain each product's own tax rate.
  if (['deposit', 'partial', 'partial-invoice', 'progress', 'progress-invoice',
    'balance', 'balance-invoice'].includes(role) || list(invoice.adjustments).length) return fallback();
  if (displays.some((display) => allMoneyRows(display).some((row) => cents(row.subtotal) < 0))) return fallback();
  const taxes = list(taxBreakdown).filter((row) => number(row.taxCents) > 0);
  if (taxes.length !== 1 || !totalsCents) return fallback();
  const tax = taxes[0];
  const rate = number(tax.rate);
  if (rate <= 0 || rate > 1) return fallback();
  if (products.some((product) => list(product.taxDetailsList).length > 1)) return fallback();
  const sameRate = (value) => Math.abs(number(value) - rate) < 1e-8;
  if (displays.some((display) => display.rate > 0 && !sameRate(display.rate))) return fallback();
  if (list(services).some((service) => number(service.taxRate) > 0 && !sameRate(service.taxRate))) return fallback();

  const productSubtotal = displays.reduce((sum, display) =>
    sum + allMoneyRows(display).reduce((nested, row) => nested + cents(row.subtotal), 0), 0);
  const serviceSubtotal = list(services).reduce((sum, service) => sum + cents(service.subtotal), 0);
  if (productSubtotal + serviceSubtotal !== number(totalsCents.subtotalCents)) return fallback();
  const taxableProductCents = displays.reduce((sum, display) =>
    sum + (sameRate(display.rate) ? allMoneyRows(display).reduce((nested, row) =>
      nested + cents(row.subtotal), 0) : 0), 0);
  const taxableServiceCents = list(services).reduce((sum, service) =>
    sum + (sameRate(service.taxRate) ? cents(service.subtotal) : 0), 0);
  const serviceTaxCents = list(services).reduce((sum, service) => sum + cents(service.tax), 0);
  if (taxableProductCents + taxableServiceCents !== number(tax.taxableSubtotalCents) ||
      number(tax.taxCents) !== number(totalsCents.taxCents)) return fallback();

  const productTaxCents = number(totalsCents.taxCents) - serviceTaxCents;
  if (productTaxCents < 0 || Math.abs(productTaxCents - Math.round(taxableProductCents * rate)) > 2) return fallback();
  // Allocate to product groups first (as the Sales Order groups prices/tax),
  // then to each size/setup row. This avoids assigning a second product
  // $34.11 tax when its own $681.95 at 5% rounds to $34.10.
  const taxableProducts = displays.filter((display) => sameRate(display.rate));
  const productTaxRows = allocateInvoiceLineTaxCents(
    taxableProducts.map((display) => ({
      subtotal: allMoneyRows(display).reduce((sum, row) => sum + cents(row.subtotal), 0) / 100,
    })),
    rate,
    productTaxCents
  );
  let productIndex = 0;
  return displays.map((display) => {
    if (!sameRate(display.rate)) {
      return setMoneyRows(display, allocateInvoiceLineTaxCents(allMoneyRows(display), 0));
    }
    const taxTarget = cents(productTaxRows[productIndex].taxAmount);
    productIndex += 1;
    return setMoneyRows(display, allocateInvoiceLineTaxCents(allMoneyRows(display), rate, taxTarget));
  });
};
